import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  TextField,
  Box,
  Button,
  Alert,
  Card,
  CardContent,
  Typography,
  LinearProgress,
} from "@mui/material";

const ResetPassword = () => {
  const [params] = useSearchParams();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [strength, setStrength] = useState("");
  const [countdown, setCountdown] = useState(0);
  const [alert, setAlert] = useState({ type: "", text: "" });

  const token = params.get("token");
  const navigate = useNavigate();

  const validatePassword = (pwd) => {
    const minLength = /.{8,}/;
    const hasUpper = /[A-Z]/;
    const hasLower = /[a-z]/;
    const hasNumber = /\d/;
    const hasSpecial = /[@$!%*?&#]/;

    return (
      minLength.test(pwd) &&
      hasUpper.test(pwd) &&
      hasLower.test(pwd) &&
      hasNumber.test(pwd) &&
      hasSpecial.test(pwd)
    );
  };

  const getStrength = (pwd) => {
    if (!pwd) return "";
    let score = 0;
    if (/.{8,}/.test(pwd)) score++;
    if (/[A-Z]/.test(pwd)) score++;
    if (/[a-z]/.test(pwd)) score++;
    if (/\d/.test(pwd)) score++;
    if (/[@$!%*?&#]/.test(pwd)) score++;

    if (score <= 2) return "Weak";
    if (score === 3 || score === 4) return "Medium";
    if (score === 5) return "Strong";
  };

  useEffect(() => {
    setStrength(getStrength(password));
  }, [password]);

  const handleReset = async () => {
    if (!password || password !== confirm) {
      setAlert({ type: "error", text: "Passwords do not match." });
      return;
    }

    if (!validatePassword(password)) {
      setAlert({
        type: "warning",
        text: "Password must be at least 8 characters and include uppercase, lowercase, number, and special character.",
      });
      return;
    }

    try {
      setLoading(true);
      const res = await fetch(
        "http://localhost:5000/api/users/reset-password",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token, password }),
        }
      );
      const data = await res.json();

      if (!res.ok) throw new Error(data.error || "Reset failed");

      setAlert({
        type: "success",
        text: "Password reset successful. Redirecting...",
      });
      setCountdown(3);
    } catch (err) {
      setAlert({
        type: "error",
        text: err.message || "Reset failed. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
    if (countdown === 0 && alert.type === "success") {
      navigate("/login");
    }
  }, [countdown, alert.type, navigate]);

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleReset();
    }
  };

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
      bgcolor="#f5f5f5"
      px={2}
    >
      <div>
        <Card sx={{ maxWidth: 400, width: "100%", boxShadow: 4 }}>
          <CardContent>
            <Typography
              variant="h5"
              align="center"
              fontWeight="bold"
              gutterBottom
            >
              Set New Password
            </Typography>

            {alert.text && (
              <Alert severity={alert.type ? "success" : "error"} sx={{ mb: 2 }}>
                {alert.text}{" "}
                {countdown > 0 && `Redirecting in ${countdown}s...`}
              </Alert>
            )}

            <TextField
              label="New Password"
              type="password"
              fullWidth
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={handleKeyPress}
              autoFocus
              sx={{ mb: 2 }}
            />

            {strength && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" gutterBottom>
                  Strength: {strength}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={
                    strength === "Weak" ? 33 : strength === "Medium" ? 66 : 100
                  }
                  color={
                    strength === "Weak"
                      ? "error"
                      : strength === "Medium"
                      ? "warning"
                      : "success"
                  }
                />
              </Box>
            )}

            <TextField
              label="Confirm Password"
              type="password"
              fullWidth
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              onKeyDown={handleKeyPress}
              sx={{ mb: 2 }}
            />

            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleReset}
              disabled={loading}
            >
              {loading ? "Resetting..." : "Reset Password"}
            </Button>
          </CardContent>
        </Card>
      </div>
    </Box>
  );
};

export default ResetPassword;
